import { useEffect, useState } from "react";
import { Loader2, Paperclip, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import GoogleBilderHinweis from "@/components/GoogleBilderHinweis";

/**
 * MailAnhaengeBereich (A-010 M3, 16.10.2026) — „Anhänge ins Projekt“ in der Mail-Zentrale.
 * Gleiche Routen und gleicher Ablauf wie im Mailverlauf (MailHistoryModal, Stufe 1 vom 27.09.2026):
 * /mail-anhaenge/pruefen, /uebernehmen, /rueckgaengig. Der Mailverlauf selbst bleibt unverändert.
 * Props: { meta: {account_id, folder, uid}, email (Kundenadresse für die Projektliste), kunde (für „Kundin um die Bilder bitten“, A-026) }
 */
const standardAuswahl = (daten, projektId) => {
  const doppelt = new Set(((daten?.duplikate || {})[projektId]) || []);
  const a = {};
  (daten?.anhaenge || []).forEach((x) => {
    a[x.index] = x.art !== "sonstig" && !x.zu_gross && !x.klein && !doppelt.has(x.index) && !x.eigenes_dokument && !daten?.eigene_mail;
  });
  return a;
};

const MailAnhaengeBereich = ({ meta, email = "", kunde = null }) => {
  const navigate = useNavigate();
  const [anh, setAnh] = useState(null);
  const [laden, setLaden] = useState(false);
  const [ziel, setZiel] = useState("");
  const [auswahl, setAuswahl] = useState({});
  const [busy, setBusy] = useState(false);
  const [fertig, setFertig] = useState(null);
  const [zeigeSig, setZeigeSig] = useState(false);
  const [unterschrieben, setUnterschrieben] = useState(false);
  const key = meta ? `${meta.account_id}/${meta.folder}/${meta.uid}` : "";

  const pruefen = async (behalteZiel = "") => {
    if (!meta) return;
    setLaden(true);
    try {
      const r = await api.post("/module-mail-inbox/mail-anhaenge/pruefen", { ...meta, email });
      const aktive = (r.data?.projekte || []).filter((p) => !p.archiv);
      const pid = behalteZiel || (aktive.length === 1 ? aktive[0].id : "");
      setAnh({ ...r.data, _key: `${meta.account_id}/${meta.folder}/${meta.uid}` });
      setZiel(pid);
      setAuswahl(standardAuswahl(r.data, pid));
      setUnterschrieben(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anhänge prüfen fehlgeschlagen");
    } finally {
      setLaden(false);
    }
  };

  useEffect(() => {
    setAnh(null);
    setFertig(null);
    pruefen();
    // eslint-disable-next-line
  }, [key, email]);

  const anzahl = Object.values(auswahl).filter(Boolean).length;
  const zielTitel = (anh?.projekte || []).find((p) => p.id === ziel)?.titel || "";

  const uebernehmen = async () => {
    const indices = Object.keys(auswahl).filter((k) => auswahl[k]).map(Number);
    if (!indices.length || !ziel) return;
    setBusy(true);
    try {
      const r = await api.post("/module-mail-inbox/mail-anhaenge/uebernehmen", { ...meta, projekt_id: ziel, indices, unterschrieben });
      toast.success(`${r.data.anzahl} Datei(en) übernommen`);
      await pruefen(ziel);
      setFertig(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Übernahme fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const rueckgaengig = async () => {
    if (!fertig) return;
    if (!window.confirm(`Die ${fertig.anzahl} eben übernommenen Datei(en) wieder aus dem Projekt „${fertig.projekt_titel}" entfernen?`)) return;
    setBusy(true);
    try {
      await api.post("/module-mail-inbox/mail-anhaenge/rueckgaengig", { projekt_id: fertig.projekt_id, uebernahme_id: fertig.uebernahme_id });
      toast.success("Übernahme rückgängig gemacht");
      setFertig(null);
      await pruefen(ziel);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rückgängig fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border-t pt-2 space-y-2" data-testid="zentrale-anhaenge">
      <div className="text-xs text-muted-foreground flex items-center gap-1">
        <Paperclip className="w-3.5 h-3.5" /> Anhänge
        {laden && <Loader2 className="w-3.5 h-3.5 animate-spin ml-1" />}
      </div>
      {anh && anh._key === key && (anh.google_drive || []).length > 0 && (
        <GoogleBilderHinweis links={anh.google_drive} kunde={kunde} projekt={(anh.projekte || []).find((p) => p.id === ziel) || null} />
      )}
      {anh && anh._key === key && ((anh.anhaenge || []).length === 0 ? (
        (anh.google_drive || []).length === 0 && <div className="text-xs text-muted-foreground">Keine Anhänge.</div>
      ) : (
        <div className="space-y-2">
          {anh.eigene_mail && (
            <div className="text-xs border border-border rounded-sm p-2 bg-muted/40 text-muted-foreground">
              Eigene Mail – die Anhänge (Angebote, Auftragsbestätigungen, Rechnungen) liegen bereits in der Suite und werden nicht übernommen.
            </div>
          )}
          {(anh.projekte || []).length === 0 ? (
            <div className="text-xs text-amber-700 dark:text-amber-300">
              {email ? "Für diesen Kunden gibt es noch kein Projekt – bitte zuerst ein Projekt anlegen." : "Absender ist noch kein Kunde – zum Übernehmen erst als Kunde anlegen oder zuordnen."}
            </div>
          ) : (
            <div className="space-y-1">
              <div className="text-xs text-muted-foreground">
                In welches Projekt? {!ziel && <span className="text-amber-700 dark:text-amber-300 font-medium">Bitte auswählen</span>}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {anh.projekte.map((p) => (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => { setZiel(p.id); setAuswahl(standardAuswahl(anh, p.id)); }}
                    className={`text-left border rounded-sm px-2 py-1.5 text-xs ${ziel === p.id ? "border-emerald-600 ring-1 ring-emerald-600 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100" : "border-border bg-background text-foreground hover:bg-muted"} ${p.archiv ? "opacity-60" : ""}`}
                    data-testid={`zentrale-anhaenge-projekt-${p.id}`}
                  >
                    <div className="font-medium truncate">{ziel === p.id ? "✓ " : ""}{p.titel || "(ohne Titel)"}</div>
                    <div className="text-muted-foreground truncate">{[p.status, p.erstellt, `${p.bilder_anzahl} Bilder`].filter(Boolean).join(" · ")}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {anh.anhaenge.filter((a) => !a.signatur || zeigeSig).map((a) => {
              const doppelt = ((anh.duplikate || {})[ziel] || []).includes(a.index);
              const moeglich = a.art !== "sonstig" && !a.zu_gross && !anh.eigene_mail && (anh.projekte || []).length > 0;
              return (
                <label key={a.index} className={`border border-border rounded-sm p-1.5 text-[11px] flex flex-col gap-1 ${moeglich ? "cursor-pointer" : "opacity-60"}`}>
                  <div className="flex items-center gap-1 min-w-0">
                    <input type="checkbox" disabled={!moeglich} checked={!!auswahl[a.index] && moeglich}
                      onChange={(e) => setAuswahl({ ...auswahl, [a.index]: e.target.checked })} />
                    <span className="truncate" title={a.filename}>{a.filename}</span>
                  </div>
                  {a.vorschau ? (
                    <img src={a.vorschau} alt="" className="w-full h-20 object-cover rounded-sm" />
                  ) : (
                    <div className="h-20 flex items-center justify-center bg-muted/40 rounded-sm"><FileText className="w-6 h-6 text-muted-foreground" /></div>
                  )}
                  <div className="text-muted-foreground">{Math.round((a.size || 0) / 1024)} KB{a.klein ? " · vermutlich Logo" : ""}</div>
                  {doppelt && <div className="text-amber-700 dark:text-amber-300 font-medium">schon im Projekt</div>}
                  {a.eigenes_dokument && <div className="text-muted-foreground">Eigenes Dokument{a.dokument_nr ? ` (${a.dokument_nr})` : ""} – liegt bereits in der Suite</div>}
                  {a.zu_gross && <div className="text-muted-foreground">zu groß (max 15 MB)</div>}
                  {a.art === "sonstig" && <div className="text-muted-foreground">kein Bild/PDF</div>}
                </label>
              );
            })}
          </div>
          {anh.anhaenge.some((a) => a.signatur) && (
            <button type="button" onClick={() => setZeigeSig(!zeigeSig)} className="text-xs underline text-muted-foreground">
              {zeigeSig ? "Vermutliche Signaturen wieder ausblenden" : `${anh.anhaenge.filter((a) => a.signatur).length} weitere Datei(en) (vermutlich Signatur/Logo) anzeigen`}
            </button>
          )}
          {(anh.projekte || []).length > 0 && !anh.eigene_mail && (
            <>
              <label className="flex items-center gap-2 text-xs cursor-pointer">
                <input type="checkbox" checked={unterschrieben} onChange={(e) => setUnterschrieben(e.target.checked)} />
                <span>✍ Unterschriebenes Dokument (Angebot/Auftrag unterschrieben zurück)</span>
              </label>
              <button
                onClick={uebernehmen}
                disabled={busy || anzahl === 0 || !ziel}
                className="w-full px-3 py-2 text-sm bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50"
                data-testid="btn-zentrale-anhaenge-uebernehmen"
              >
                {busy ? "Bitte warten – Dateien werden einzeln verarbeitet…" : !ziel ? "Bitte zuerst oben ein Projekt auswählen" : `${anzahl} Datei(en) ins Projekt „${zielTitel || "ohne Titel"}" übernehmen`}
              </button>
            </>
          )}
          {fertig && (
            <div className="border border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200 rounded-sm p-2 text-xs space-y-2">
              <div>
                ✅ {fertig.anzahl} Datei(en) ins Projekt „{fertig.projekt_titel || "ohne Titel"}" übernommen
                {(fertig.uebersprungen || []).length ? ` · ${fertig.uebersprungen.length} übersprungen` : ""}.
              </div>
              {(fertig.uebersprungen || []).map((u, i) => <div key={i}>– {u.filename}: {u.grund}</div>)}
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => navigate(`/module/projekte/werkbank/${fertig.kunde_id}?projekt_id=${fertig.projekt_id}`)} className="px-2 py-1 border border-border rounded-sm bg-background text-foreground hover:bg-muted">Zum Projekt</button>
                <button onClick={() => setFertig(null)} className="px-2 py-1 border border-border rounded-sm bg-background text-foreground hover:bg-muted">Hier bleiben</button>
                {fertig.anzahl > 0 && (
                  <button onClick={rueckgaengig} disabled={busy} className="px-2 py-1 border border-red-300 text-red-700 dark:text-red-300 rounded-sm hover:bg-red-50 dark:hover:bg-red-950/40">Rückgängig</button>
                )}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default MailAnhaengeBereich;
